<template>
  <div class="analytics-date-range-picker">
    <div class="analytics-date-range-picker__fields">
      <div class="analytics-date-range-picker__field">
        <label
          class="analytics-date-range-picker__label"
          :for="`${componentId}-start`"
        >
          {{ $t('settings.analytics.dateRange.startLabel') }}
        </label>
        <b-form-datepicker
          :id="`${componentId}-start`"
          :value="startDateAsYmd"
          :min="minDateAsYmd"
          :max="startMaxDate"
          :placeholder="$t('settings.analytics.dateRange.startLabel')"
          size="sm"
          :locale="$i18n.locale"
          :date-format-options="dateFormatOptions"
          @input="handleStartInput"
        ></b-form-datepicker>
      </div>

      <div class="analytics-date-range-picker__field">
        <label
          class="analytics-date-range-picker__label"
          :for="`${componentId}-end`"
        >
          {{ $t('settings.analytics.dateRange.endLabel') }}
        </label>
        <b-form-datepicker
          :id="`${componentId}-end`"
          :value="endDateAsYmd"
          :min="endMinDate"
          :max="todayAsYmd"
          :placeholder="$t('settings.analytics.dateRange.endLabel')"
          size="sm"
          :locale="$i18n.locale"
          :date-format-options="dateFormatOptions"
          @input="handleEndInput"
        ></b-form-datepicker>
      </div>

      <b-button
        class="analytics-date-range-picker__apply"
        variant="primary"
        size="sm"
        :disabled="!canApply"
        @click="applyRange"
      >
        {{ $t('buttons.apply') }}
      </b-button>
    </div>
  </div>
</template>

<script>
const DATE_FORMAT_OPTIONS = { year: 'numeric', month: 'short', day: 'numeric' };

export default {
  name: 'AnalyticsDateRangePicker',
  props: {
    value: {
      type: Object,
      default: () => ({
        startDate: null,
        endDate: null,
      }),
    },
    minDate: {
      type: [String, Date],
      default: null,
    },
  },
  data() {
    return {
      componentId: `analytics-date-range-picker-${this._uid}`,
      internalStartDate: null,
      internalEndDate: null,
      dateFormatOptions: DATE_FORMAT_OPTIONS,
    };
  },
  computed: {
    startDateAsYmd() {
      return this.internalStartDate;
    },
    endDateAsYmd() {
      return this.internalEndDate;
    },
    todayAsYmd() {
      return this.formatLocalYmd(new Date());
    },
    minDateAsYmd() {
      return this.toYmd(this.minDate);
    },
    startMaxDate() {
      return this.endDateAsYmd || this.todayAsYmd;
    },
    endMinDate() {
      return this.startDateAsYmd || this.minDateAsYmd;
    },
    canApply() {
      const valueStartDate = this.toYmd(this.value && this.value.startDate);
      const valueEndDate = this.toYmd(this.value && this.value.endDate);

      return Boolean(
        this.internalStartDate
          && this.internalEndDate
          && (
            this.internalStartDate !== valueStartDate
            || this.internalEndDate !== valueEndDate
          ),
      );
    },
  },
  watch: {
    value: {
      immediate: true,
      deep: true,
      handler(value) {
        this.internalStartDate = this.toYmd(value && value.startDate);
        this.internalEndDate = this.toYmd(value && value.endDate);
      },
    },
  },
  methods: {
    handleStartInput(value) {
      let startDate = value || null;
      let endDate = this.endDateAsYmd;

      startDate = this.clampStartToMinDate(startDate);

      if (startDate && endDate && startDate > endDate) {
        endDate = startDate;
      }

      this.internalStartDate = startDate;
      this.internalEndDate = endDate;
    },
    handleEndInput(value) {
      let startDate = this.startDateAsYmd;
      let endDate = value || null;

      if (endDate && endDate > this.todayAsYmd) {
        endDate = this.todayAsYmd;
      }

      if (this.minDateAsYmd && endDate && endDate < this.minDateAsYmd) {
        endDate = this.minDateAsYmd;
      }

      if (startDate && endDate && endDate < startDate) {
        startDate = endDate;
      }

      this.internalStartDate = startDate;
      this.internalEndDate = endDate;
    },
    applyRange() {
      if (!this.canApply) {
        return;
      }

      this.$emit('input', {
        startDate: this.toIsoString(this.internalStartDate, 'start'),
        endDate: this.toIsoString(this.internalEndDate, 'end'),
      });
    },
    clampStartToMinDate(startDateAsYmd) {
      if (!startDateAsYmd || !this.minDateAsYmd) {
        return startDateAsYmd;
      }

      return startDateAsYmd < this.minDateAsYmd ? this.minDateAsYmd : startDateAsYmd;
    },
    toIsoString(dateAsYmd, dayBoundary) {
      const date = this.parseYmd(dateAsYmd);

      if (!date) {
        return null;
      }

      if (dayBoundary === 'end') {
        date.setHours(23, 59, 59, 0);
      } else {
        date.setHours(0, 0, 0, 0);
      }

      return date.toISOString();
    },
    toYmd(value) {
      if (!value) {
        return null;
      }

      if (typeof value === 'string' && /^\d{4}-\d{2}-\d{2}$/.test(value)) {
        return value;
      }

      const date = value instanceof Date
        ? new Date(value.getTime())
        : new Date(value);

      if (!this.isValidDate(date)) {
        return null;
      }

      return this.formatLocalYmd(date);
    },
    parseYmd(value) {
      if (!value) {
        return null;
      }

      const parts = value.split('-').map((part) => Number(part));
      const date = new Date(parts[0], parts[1] - 1, parts[2]);

      return this.isValidDate(date) ? date : null;
    },
    formatLocalYmd(date) {
      const year = date.getFullYear();
      const month = String(date.getMonth() + 1).padStart(2, '0');
      const day = String(date.getDate()).padStart(2, '0');

      return `${year}-${month}-${day}`;
    },
    isValidDate(date) {
      return date instanceof Date && !Number.isNaN(date.getTime());
    },
  },
};
</script>

<style scoped lang="scss">
.analytics-date-range-picker {
  display: inline-flex;
  align-items: flex-end;
  padding-bottom: 0.75rem;
  color: #b9b9b9;
}

.analytics-date-range-picker__fields {
  width: auto;
  display: flex;
  align-items: flex-end;
  gap: 0.5rem;
}

.analytics-date-range-picker__field {
  flex: 0 0 160px;
  max-width: 160px;
}

.analytics-date-range-picker__label {
  display: block;
  margin-bottom: 0.25rem;
  color: #a9b1b5;
  font-size: 12px;
  font-weight: 600;
  line-height: 1.2;
  text-transform: uppercase;
}

.analytics-date-range-picker__apply {
  flex: 0 0 auto;
  margin-bottom: 1px;
  white-space: nowrap;
}

::v-deep .b-form-datepicker {
  width: 100%;
  max-width: 160px;
  background-color: #53575B;
  border-color: #53575B;
  border-radius: 0.25rem;

  label {
    background-color: #53575B;
    color: #fff;
    border-color: #53575B;
  }
}

@media (max-width: 480px) {
  .analytics-date-range-picker {
    width: 100%;
    display: flex;
  }

  .analytics-date-range-picker__fields {
    width: 100%;
    flex-direction: column;
  }

  .analytics-date-range-picker__field {
    flex-basis: auto;
    max-width: none;
  }

  .analytics-date-range-picker__apply {
    width: 100%;
  }
}
</style>
